import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Card,
  Button,
  Space,
  message,
  DatePicker,
  Tooltip,
  Tag,
  Modal,
} from "antd";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import AgGridWithColumnState from "../../../components/AgGridWithColumnState";
import {
  RollbackOutlined,
  ExclamationCircleOutlined,
  DownloadOutlined,
  EditOutlined,
} from "@ant-design/icons";
import moment from "moment";
import {
  getTransactionHistory,
  rollbackTransaction,
  getOverpaymentAllocationHistory,
  rollbackOverpaymentAllocation,
} from "../../api/accounting";

const getDecimalPrecision = () => {
  try { const s = localStorage.getItem("global_decimal_precision"); return s ? parseInt(s, 10) : 3; } catch { return 3; }
};

// Get global display currency settings
const getGlobalDisplayCurrency = () => {
  try {
    const currency = localStorage.getItem('global_display_currency') || 'USD';
    const SYMBOLS = { USD: '$', EUR: '€', GBP: '£', JPY: '¥', CAD: 'C$', AUD: 'A$', CHF: 'CHF', INR: '₹' };
    const symbol = SYMBOLS[currency] || currency;
    if (currency === 'USD') return { currency, symbol, rate: 1 };
    const ratesStr = localStorage.getItem('custom_currency_rates');
    const rates = ratesStr ? JSON.parse(ratesStr) : {};
    return { currency, symbol, rate: rates[currency] || null };
  } catch (e) {
    return { currency: 'USD', symbol: '$', rate: 1 };
  }
};

// Formatter using global display currency
const formatCurrencyPlain = (amount, originalCurrency) => {
  const value = parseFloat(amount || 0);
  const dp = getDecimalPrecision();
  if (Number.isNaN(value)) return `$${(0).toFixed(dp)}`;
  
  // Use global display currency
  const { symbol: displaySymbol, rate, currency: displayCurrency } = getGlobalDisplayCurrency();
  
  if (displayCurrency !== 'USD' && rate && rate > 0) {
    const converted = value * rate;
    return `${displaySymbol}${converted.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
  }
  
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
};

const { RangePicker } = DatePicker;

const TransactionsHistory = ({ selectedBrand = null }) => {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rollbackLoading, setRollbackLoading] = useState(false);
  const [dateFilter, setDateFilter] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const gridApiRef = useRef(null);

  // Cache key for persistence
  const cacheKey = selectedBrand
    ? `transactions-history-cache-${selectedBrand.id}`
    : null;

  // Load from cache on mount - use ref to avoid re-renders
  const cachedDataRef = useRef(null);
  const [hasLoadedFromCache, setHasLoadedFromCache] = useState(false);
  const fetchingRef = useRef(false); // Prevent multiple simultaneous requests
  const lastFetchKeyRef = useRef(null); // Track last fetch to prevent duplicates
  const fetchHistoryRef = useRef(null); // Store latest fetch function

  // Initialize cache on mount and when page changes
  useEffect(() => {
    if (!cacheKey) {
      cachedDataRef.current = null;
      setHasLoadedFromCache(false);
      return;
    }
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        const cacheAge = Date.now() - (parsed.timestamp || 0);
        // Cache valid for 5 minutes
        if (cacheAge < 5 * 60 * 1000) {
          cachedDataRef.current = parsed.data;
          // Load current page from cache immediately if available
          const cacheKeyForPage = `page-${currentPage}-size-${pageSize}`;
          if (parsed.data && parsed.data[cacheKeyForPage]) {
            const cached = parsed.data[cacheKeyForPage];
            setHistoryData(cached.results || []);
            setTotalCount(cached.count || 0);
            setHasLoadedFromCache(true);
            setLoading(false);
            return;
          }
        } else {
          // Cache expired, clear it
          cachedDataRef.current = null;
        }
      }
    } catch (e) {
      console.warn("Failed to load cache:", e);
    }
    if (!cachedDataRef.current) {
      setHasLoadedFromCache(false);
    }
  }, [cacheKey, currentPage, pageSize]);

  // Fetch transaction history with pagination
  const fetchHistory = useCallback(
    async (page = currentPage, size = pageSize, forceRefresh = false) => {
      if (!selectedBrand) return;

      // Create unique key for this fetch request
      const fetchKey = `${selectedBrand.id}-${page}-${size}`;

      // Prevent duplicate simultaneous requests
      if (fetchingRef.current) {
        console.log("Already fetching, skipping duplicate request:", fetchKey);
        return;
      }

      // If we just fetched this exact same data, skip
      if (!forceRefresh && lastFetchKeyRef.current === fetchKey) {
        console.log("Already fetched this data, skipping:", fetchKey);
        return;
      }

      // Check cache first if not forcing refresh
      const cacheKeyForPage = `page-${page}-size-${size}`;
      if (
        !forceRefresh &&
        cacheKey &&
        cachedDataRef.current &&
        cachedDataRef.current[cacheKeyForPage]
      ) {
        const cached = cachedDataRef.current[cacheKeyForPage];
        setHistoryData(cached.results || []);
        setTotalCount(cached.count || 0);
        setLoading(false);
        lastFetchKeyRef.current = fetchKey;
        return;
      }

      // Mark as fetching
      fetchingRef.current = true;
      lastFetchKeyRef.current = fetchKey;

      try {
        setLoading(true);

        // Fetch both histories in parallel to speed up loading
        const [transactionResponse, overpaymentResponse] = await Promise.all([
          getTransactionHistory({
            brand: selectedBrand.id,
            page: page,
            page_size: size,
          }).catch((err) => {
            console.error("Error fetching transaction history:", err);
            return { results: [], count: 0 };
          }),
          getOverpaymentAllocationHistory({
            brand: selectedBrand.id,
            page: page,
            page_size: size,
          }).catch((err) => {
            console.error("Error fetching overpayment allocation history:", err);
            return { results: [], count: 0 };
          }),
        ]);

        // Merge transaction and overpayment allocation history
        const transactions =
          transactionResponse?.results ||
          transactionResponse?.data?.results ||
          [];
        const overpayments =
          overpaymentResponse?.results ||
          overpaymentResponse?.data?.results ||
          [];

        // Calculate total count (sum of both types)
        const transactionCount = transactionResponse?.count || 0;
        const overpaymentCount = overpaymentResponse?.count || 0;
        const combinedTotal = transactionCount + overpaymentCount;

        // Transform overpayment allocation history to match transaction history format
        // Optimize description building to avoid multiple string concatenations
        const transformedOverpayments = overpayments.map((op) => {
          const parts = ["Overpayment Allocation"];
          if (op.overpayment_number) parts.push(` - ${op.overpayment_number}`);
          if (op.invoice_number) parts.push(` to Invoice ${op.invoice_number}`);
          if (op.target_overpayment_number)
            parts.push(` to Overpayment ${op.target_overpayment_number}`);
          
          return {
            ...op,
            history_type: op.history_type || "+",
            history_date: op.history_date,
            history_id: op.history_id,
            history_username:
              op.history_username || op.history_user?.username || null,
            history_change_reason: op.history_change_reason,
            field_changes: op.field_changes || {},
            bank_name: op.bank_name || "N/A",
            transaction_date: op.allocation_date || op.created_at,
            amount: op.amount,
            description: parts.join(""),
            is_overpayment_allocation: true,
          };
        });

        // Combine transactions and overpayment allocations
        const allHistory = [...transactions, ...transformedOverpayments];

        // Deduplicate by history_id and type (in case of duplicates) - optimized
        const seen = new Set();
        const deduplicated = [];
        for (const item of allHistory) {
          const key = `${item.history_id || item.id}-${
            item.is_overpayment_allocation ? "op" : "txn"
          }`;
          if (!seen.has(key)) {
            seen.add(key);
            deduplicated.push(item);
          }
        }

        // Sort by history_date (newest first) - optimized with pre-computed timestamps
        const sorted = deduplicated.sort((a, b) => {
          const dateA = a.history_date ? moment(a.history_date).unix() : 0;
          const dateB = b.history_date ? moment(b.history_date).unix() : 0;
          return dateB - dateA;
        });

        setHistoryData(sorted);
        setTotalCount(combinedTotal);

        // Cache the results
        if (cacheKey) {
          try {
            const cacheData = cachedDataRef.current || {};
            cacheData[`page-${page}-size-${size}`] = {
              results: sorted,
              count: combinedTotal,
              timestamp: Date.now(),
            };
            cachedDataRef.current = cacheData;
            localStorage.setItem(
              cacheKey,
              JSON.stringify({ data: cacheData, timestamp: Date.now() })
            );
          } catch (e) {
            console.warn("Failed to save cache:", e);
          }
        }
      } catch (err) {
        message.error(
          "Failed to fetch history: " +
            (err.response?.data?.error ||
              err.response?.data?.detail ||
              err.message)
        );
        console.error("Error fetching history:", err);
        setHistoryData([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
        fetchingRef.current = false;
      }
    },
    [selectedBrand, currentPage, pageSize, cacheKey]
  );

  // Keep ref updated with latest fetchHistory
  useEffect(() => {
    fetchHistoryRef.current = fetchHistory;
  }, [fetchHistory]);

  // Handle pagination changes
  const handlePaginationChange = useCallback(
    (newPage, newPageSize) => {
      setCurrentPage(newPage);
      if (newPageSize !== pageSize) {
        setPageSize(newPageSize);
      }
    },
    [pageSize]
  );

  // Export functionality for Transaction History
  const exportToExcel = useCallback(
    async (returnFile = false) => {
      try {
        const exportFilters = {
          brand: selectedBrand ? selectedBrand.id : null,
          date: dateFilter || null,
        };

        console.log(
          "Exporting transaction history with filters:",
          exportFilters
        );

        const response = await fetch(
          "/api/accounting/transactions/history/export/",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            },
            body: JSON.stringify(exportFilters),
          }
        );

        if (!response.ok) {
          throw new Error(`Export failed: ${response.statusText}`);
        }

        const blob = await response.blob();

        if (returnFile) {
          // Return blob for email sharing
          const timestamp = new Date()
            .toISOString()
            .slice(0, 19)
            .replace(/[:-]/g, "");
          const filename = `transaction_history_export_${timestamp}.xlsx`;
          return new File([blob], filename, {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
        } else {
          // Direct download
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;

          const timestamp = new Date()
            .toISOString()
            .slice(0, 19)
            .replace(/[:-]/g, "");
          link.download = `transaction_history_export_${timestamp}.xlsx`;

          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          message.success("Transaction history exported successfully!");
        }
      } catch (error) {
        console.error("Export error:", error);
        message.error(`Export failed: ${error.message}`);
        throw error;
      }
    },
    [selectedBrand, dateFilter]
  );

  // Fetch transaction history when selected brand or pagination changes
  // Only fetch if we haven't loaded from cache or if brand/page changed
  useEffect(() => {
    if (!selectedBrand) {
      setHistoryData([]);
      setTotalCount(0);
      setHasLoadedFromCache(false);
      lastFetchKeyRef.current = null;
      return;
    }

    // If we just loaded from cache, don't fetch again
    if (hasLoadedFromCache) {
      setHasLoadedFromCache(false);
      return;
    }

    // Fetch immediately - no delay needed since we have duplicate prevention
    if (!fetchingRef.current && fetchHistoryRef.current) {
      fetchHistoryRef.current(currentPage, pageSize, false);
    }
  }, [selectedBrand, currentPage, pageSize]);

  // AG Grid column definitions matching Action History module
  const columnDefs = [
    {
      headerName: "Date",
      field: "history_date",
      sortable: true,
      filter: true,
      width: 180,
      valueFormatter: (params) => {
        return params.value
          ? moment(params.value).format("YYYY-MM-DD HH:mm:ss")
          : "";
      },
    },
    {
      headerName: "Bank",
      field: "bank_name",
      sortable: true,
      filter: true,
      width: 120,
      valueGetter: (params) => {
        return params.data?.bank_name || "N/A";
      },
    },
    {
      headerName: "Partner",
      field: "partner_name",
      sortable: true,
      filter: true,
      width: 200,
      valueGetter: (params) => {
        // Use partner_name from serializer if available, otherwise try to get from partner object
        return (
          params.data?.partner_name ||
          params.data?.partner?.name ||
          (params.data?.partner_id
            ? `Partner ${params.data.partner_id}`
            : "N/A")
        );
      },
    },
    {
      headerName: "Description",
      field: "description",
      sortable: true,
      filter: true,
      width: 250,
      valueGetter: (params) => {
        // Handle case where description might be an object
        let desc = params.data?.description;
        if (typeof desc === "object" && desc !== null) {
          desc = desc.description || JSON.stringify(desc);
        }
        // Use description field if available, otherwise construct from other fields
        if (desc) return String(desc);
        if (params.data?.is_overpayment_allocation) {
          return `Overpayment Allocation${
            params.data.overpayment_number
              ? ` - ${params.data.overpayment_number}`
              : ""
          }`;
        }
        return "-";
      },
    },
    {
      headerName: "Transaction Date",
      field: "transaction_date",
      sortable: true,
      filter: true,
      width: 120,
      valueFormatter: (params) => {
        return params.value ? moment(params.value).format("YYYY-MM-DD") : "";
      },
    },
    {
      headerName: "Amount",
      field: "amount",
      sortable: true,
      filter: "agNumberColumnFilter",
      width: 150,
      cellRenderer: (params) => {
        const amount = params.value;
        if (amount == null) return "$0.00";
        // Get currency from data - check multiple possible locations
        const currency =
          params.data?.currency || params.data?.originalData?.currency || "$";
        return formatCurrencyPlain(amount, currency);
      },
    },
    {
      headerName: "Action",
      field: "history_type",
      sortable: true,
      filter: true,
      width: 120,
      cellRenderer: (params) => {
        const type = params.value;
        const fieldChanges = params.data?.field_changes || {};
        const isRollback =
          fieldChanges.rollback || fieldChanges.explicit_rollback;

        // Handle both formatted text and symbol formats
        if (type === "Created" || type === "+") {
          return <Tag color="green">Created</Tag>;
        } else if (type === "Updated" || type === "~") {
          // Check if this is a rollback operation
          if (isRollback) {
            return <Tag color="orange">Rolled Back</Tag>;
          }
          return <Tag color="blue">Updated</Tag>;
        } else if (type === "Deleted" || type === "-") {
          return <Tag color="red">Deleted</Tag>;
        }
        return <Tag>{type || "Unknown"}</Tag>;
      },
    },
    {
      headerName: "User",
      field: "history_username",
      sortable: true,
      filter: true,
      width: 200,
      valueGetter: (params) => {
        return (
          params.data?.history_username ||
          params.data?.history_user?.username ||
          "System"
        );
      },
    },
    {
      headerName: "Changes",
      field: "field_changes",
      sortable: false,
      filter: false,
      width: 150,
      cellRenderer: (params) => {
        const changes = params.value;
        if (
          !changes ||
          (typeof changes === "object" && Object.keys(changes).length === 0)
        ) {
          return <Tag color="default">No changes</Tag>;
        }

        // Handle created records
        if (changes.created) {
          const isOverpayment = params.data?.is_overpayment_allocation;
          return (
            <Tag color="green">
              {isOverpayment
                ? "Overpayment allocation created"
                : "Transaction created"}
            </Tag>
          );
        }

        // Handle deleted records
        if (changes.deleted) {
          const isOverpayment = params.data?.is_overpayment_allocation;
          return (
            <Tag color="red">
              {isOverpayment
                ? "Overpayment allocation deleted"
                : "Transaction deleted"}
            </Tag>
          );
        }

        // Handle rollback
        if (changes.rollback) {
          return <Tag color="orange">Rollback operation</Tag>;
        }

        // Filter out metadata fields
        const displayableChanges = Object.entries(changes).filter(
          ([key]) =>
            ![
              "created",
              "deleted",
              "rollback",
              "rollback_source",
              "explicit_rollback",
              "history_user",
              "history_date",
              "history_change_reason",
            ].includes(key)
        );

        if (displayableChanges.length === 0) {
          return changes.rollback ? (
            <Tag color="orange">Rollback operation</Tag>
          ) : (
            <Tag color="default">No changes</Tag>
          );
        }

        const format = (key, val) => {
          if (val === null || val === undefined) return "-";
          if (key.includes("date")) {
            return moment(val).isValid()
              ? moment(val).format("YYYY-MM-DD")
              : String(val);
          }
          if (["amount", "allocated_amount"].includes(key)) {
            const num = parseFloat(val);
            return isNaN(num) ? String(val) : `$${num.toFixed(getDecimalPrecision())}`;
          }
          return String(val);
        };

        return (
          <Space wrap size={4}>
            {displayableChanges.map(([key, change]) => {
              // change may be { old, new } or a simple value
              let oldVal =
                typeof change === "object" && change !== null
                  ? format(key, change.old)
                  : format(key, null);
              let newVal =
                typeof change === "object" && change !== null
                  ? format(key, change.new)
                  : format(key, change);

              // Handle partner field - show partner name instead of ID
              if (key === "partner" || key === "partner_id") {
                // Try to get partner name from the data
                const partnerName =
                  params.data?.partner_name ||
                  params.data?.partner?.name ||
                  (typeof change.old === "object" && change.old?.name) ||
                  (typeof change.new === "object" && change.new?.name);

                if (partnerName) {
                  oldVal =
                    typeof change === "object" && change !== null && change.old
                      ? typeof change.old === "object"
                        ? change.old.name || change.old.id
                        : partnerName
                      : params.data?.partner_name || "N/A";
                  newVal =
                    typeof change === "object" && change !== null && change.new
                      ? typeof change.new === "object"
                        ? change.new.name || change.new.id
                        : partnerName
                      : params.data?.partner_name || "N/A";
                } else {
                  // Fallback to ID if name not available
                  oldVal =
                    typeof change === "object" && change !== null && change.old
                      ? typeof change.old === "object"
                        ? change.old.id || change.old
                        : String(change.old || "N/A")
                      : "N/A";
                  newVal =
                    typeof change === "object" && change !== null && change.new
                      ? typeof change.new === "object"
                        ? change.new.id || change.new
                        : String(change.new || "N/A")
                      : "N/A";
                }
              }

              const isAlloc =
                key === "allocated_amount" ||
                key === "allocation_status" ||
                key === "partner" ||
                key === "partner_id" ||
                key === "invoice_id" ||
                key === "overpayment_id" ||
                key === "target_overpayment_id";
              const color = isAlloc ? "blue" : "default";
              return (
                <Tag key={key} color={color}>
                  {key.replace(/_/g, " ")}: {oldVal} → {newVal}
                </Tag>
              );
            })}
          </Space>
        );
      },
    },
    {
      headerName: "Change Reason",
      field: "history_change_reason",
      sortable: true,
      filter: true,
      width: 150,
      valueFormatter: (params) => {
        return params.value
          ? `Rollbacked from ${moment(params.value).format(
              "YYYY-MM-DD HH:mm:ss"
            )}`
          : "N/A";
      },
    },
    {
      headerName: "Actions",
      field: "actions",
      width: 120,
      pinned: "right",
      cellRenderer: (params) => {
        const isOverpaymentAllocation =
          params.data?.is_overpayment_allocation || false;
        const isDeletion =
          params.data?.history_type === "-" ||
          params.data?.history_type === "Deleted";

        return (
          <Button
            type="primary"
            icon={<RollbackOutlined />}
            size="small"
            onClick={() =>
              handleRollback(
                params.data.history_id || params.data.id,
                params.data.transaction_date || params.data.history_date,
                isOverpaymentAllocation
              )
            }
            loading={rollbackLoading}
            disabled={rollbackLoading || !params.data.history_id || isDeletion}
          >
            Rollback
          </Button>
        );
      },
    },
  ];

  // Default column definition
  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    minWidth: 100,
  };

  // Handle rollback to a previous version
  const handleRollback = (
    historyId,
    recordDate,
    isOverpaymentAllocation = false
  ) => {
    Modal.confirm({
      title: "Confirm Rollback",
      icon: <ExclamationCircleOutlined />,
      width: 600,
      content: (
        <div>
          <p>
            Are you sure you want to roll back this{" "}
            {isOverpaymentAllocation ? "overpayment allocation" : "transaction"}{" "}
            to the version from{" "}
            {moment(recordDate).format("YYYY-MM-DD HH:mm:ss")}?
          </p>
          <p style={{ marginTop: 12, color: "#666", fontSize: "12px" }}>
            <strong>What rollback does:</strong> This will restore the{" "}
            {isOverpaymentAllocation ? "allocation" : "transaction"} to its
            previous state, reverse any existing journal entries, and create new
            journal entries for the rolled-back state. This action will be
            recorded in the history.
          </p>
        </div>
      ),
      okText: "Yes, Roll Back",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          setRollbackLoading(true);
          const result = isOverpaymentAllocation
            ? await rollbackOverpaymentAllocation(historyId, recordDate)
            : await rollbackTransaction(historyId, recordDate);
          const successMsg =
            result?.message ||
            result?.data?.message ||
            `${
              isOverpaymentAllocation ? "Overpayment allocation" : "Transaction"
            } rolled back successfully`;
          message.success(successMsg);
          // Refresh the history data to show updated status
          fetchHistory(currentPage, pageSize, true);
        } catch (err) {
          const errorMsg =
            err.response?.data?.detail ||
            err.response?.data?.error ||
            err.response?.data?.message ||
            err.message;
          message.error(
            `Failed to roll back ${
              isOverpaymentAllocation ? "overpayment allocation" : "transaction"
            }: ${errorMsg}`
          );
          console.error("Rollback error:", err);
        } finally {
          setRollbackLoading(false);
        }
      },
    });
  };

  return (
    <Card title="Transaction History" style={{ height: "100%" }}>
      <div
        className="ag-theme-alpine"
        style={{ height: "600px", width: "100%" }}
      >
        <AgGridWithColumnState
          tableId="transactions-history"
          gridApiRef={gridApiRef}
          columnDefs={columnDefs}
          rowData={historyData}
          defaultColDef={defaultColDef}
          loading={loading}
          pagination={true}
          paginationPageSize={pageSize}
          paginationPageSizeSelector={[25, 50, 100, 200]}
          paginationPosition="top"
          enableRangeSelection={true}
          enableCellTextSelection={true}
          ensureDomOrder={true}
          suppressMovable={false}
          domLayout="normal"
          onPaginationChanged={(params) => {
            if (params.newPage !== undefined) {
              const newPage = params.api.paginationGetCurrentPage() + 1; // AG Grid is 0-indexed
              const newPageSize = params.api.paginationGetPageSize();
              if (newPage !== currentPage || newPageSize !== pageSize) {
                handlePaginationChange(newPage, newPageSize);
              }
            }
          }}
        />
      </div>
    </Card>
  );
};

export default TransactionsHistory;
